import {
  ShippingOptionDelivery,
  ShippingOptionParcel,
  ShippingEstimate,
} from '@pedix-workspace/utils';
import {
  MinimumPurchaseErrorResponse,
  FreeShippingResponse,
  ToBeDefinedResponse,
  FixedCostResponse,
  OutOfDeliveryZoneResponse,
  ShippingEstimateResponse,
  DeliveryCostResponse,
  ParcelShippingResponse,
  PostalCodeRequiredResponse,
  ParcelShippingSelectionRequiredResponse,
} from './shipping-cost-response.types';

/**
 * Crea una respuesta de error por mínimo de compra no alcanzado
 */
export function createMinimumPurchaseErrorResponse<
  T extends ShippingOptionDelivery | ShippingOptionParcel,
>(amount: number, shippingOption: T): MinimumPurchaseErrorResponse<T> {
  return {
    type: 'minimum-purchase-error',
    minimumPurchaseErrorAmount: amount,
    shippingOption,
  };
}

/**
 * Crea una respuesta de envío gratis otorgado
 */
export function createFreeShippingResponse<T extends ShippingOptionDelivery | ShippingOptionParcel>(
  amount: number,
  shippingOption: T,
): FreeShippingResponse<T> {
  return {
    type: 'free-shipping',
    freeShippingGrantedAmount: amount,
    shippingOption,
  };
}

/**
 * Crea una respuesta de costo de envío por definir
 */
export function createToBeDefinedResponse<T extends ShippingOptionDelivery | ShippingOptionParcel>(
  shippingOption: T,
): ToBeDefinedResponse<T> {
  return {
    type: 'to-be-defined',
    deliveryCostToBeDefined: true,
    minimumPurchaseForFreeShipping: shippingOption.minimumPurchaseForFreeShipping,
    shippingOption,
  };
}

/**
 * Crea una respuesta de costo fijo de envío
 */
export function createFixedCostResponse<T extends ShippingOptionDelivery | ShippingOptionParcel>(
  cost: number | undefined,
  shippingOption: T,
): FixedCostResponse<T> {
  return {
    type: 'fixed-cost',
    deliveryCost: cost || 0,
    minimumPurchaseForFreeShipping: shippingOption.minimumPurchaseForFreeShipping,
    shippingOption,
  };
}

/**
 * Crea una respuesta de fuera de zona de entrega
 */
export function createOutOfDeliveryZoneResponse<
  T extends ShippingOptionDelivery | ShippingOptionParcel,
>(shippingOption: T): OutOfDeliveryZoneResponse<T> {
  return {
    type: 'out-of-delivery-zone',
    outOfDeliveryZone: true,
    shippingOption,
  };
}

/**
 * Crea una respuesta con estimación de envío de integraciones logísticas
 */
export function createShippingEstimateResponse<
  T extends ShippingOptionDelivery | ShippingOptionParcel,
>(
  estimate: ShippingEstimate,
  shippingOption: T,
  isFree: boolean,
  freeShippingMinimum?: number,
): ShippingEstimateResponse<T> {
  return {
    type: 'shipping-estimate',
    deliveryCost: isFree ? 0 : estimate.price,
    minimumPurchaseForFreeShipping: freeShippingMinimum,
    outOfDeliveryZone: estimate.outOfDeliveryZone || false,
    shippingEstimate: estimate,
    shippingOption,
    ...(isFree && { freeShippingGrantedAmount: freeShippingMinimum }),
  };
}

/**
 * Crea una respuesta de costo de envío basado en zonas de entrega
 */
export function createDeliveryCostResponse<T extends ShippingOptionDelivery | ShippingOptionParcel>(
  cost: number | undefined,
  freeShippingMinimum: number | undefined,
  shippingOption: T,
): DeliveryCostResponse<T> {
  return {
    type: 'delivery-cost',
    deliveryCost: cost || 0,
    minimumPurchaseForFreeShipping: freeShippingMinimum,
    shippingOption,
  };
}

/**
 * Crea una respuesta de código postal requerido para paquetería
 */
export function createPostalCodeRequiredResponse(
  shippingOption: ShippingOptionParcel,
): PostalCodeRequiredResponse {
  return {
    type: 'postal-code-required',
    postalCodeRequired: true,
    minimumPurchaseForFreeShipping: shippingOption.minimumPurchaseForFreeShipping,
    shippingOption,
  };
}

/**
 * Crea una respuesta de envío por paquetería con una opción seleccionada o auto-seleccionada
 */
export function createParcelShippingResponse(
  estimate: ShippingEstimate,
  shippingOption: ShippingOptionParcel,
  isFree = false,
): ParcelShippingResponse {
  return {
    type: 'parcel-shipping',
    availableParcelOptions: [estimate],
    requiresParcelSelection: false,
    deliveryCost: isFree ? 0 : estimate.price,
    outOfDeliveryZone: estimate.outOfDeliveryZone || false,
    shippingEstimate: estimate,
    shippingOption,
    minimumPurchaseForFreeShipping: shippingOption.minimumPurchaseForFreeShipping,
    ...(isFree && { freeShippingGrantedAmount: shippingOption.minimumPurchaseForFreeShipping }),
  };
}

/**
 * Crea una respuesta que requiere que el usuario seleccione una opción de paquetería
 */
export function createParcelShippingSelectionRequiredResponse(
  shippingOption: ShippingOptionParcel,
  availableParcelOptions: ShippingEstimate[],
): ParcelShippingSelectionRequiredResponse {
  return {
    type: 'parcel-shipping-selection-required',
    shippingOption,
    availableParcelOptions,
    requiresParcelSelection: true,
    minimumPurchaseForFreeShipping: shippingOption.minimumPurchaseForFreeShipping,
  };
}
